import React, { useState, useEffect } from 'react';
import { axiosPrivate } from '@/api/axios';
import { fetchAllUsers } from '@/api/users';
import { User, FileText, Bell, Users, Settings, Ticket } from 'lucide-react';

const DashboardPage = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([
    { label: 'Users', value: '0', positive: true },
    { label: 'Experts', value: '0', positive: true },
    { label: 'Influencers', value: '0', positive: true },
    { label: 'Notifications', value: '0', positive: true },
    { label: 'Gut Facts', value: '0', positive: true },
    { label: 'Gut Challenges', value: '0', positive: true },
    { label: 'Nutrition Facts', value: '0', positive: true },
    { label: 'Nutrition Challenges', value: '0', positive: true },
    { label: 'Sleep Facts', value: '0', positive: true },
    { label: 'Exercise Activities', value: '0', positive: true },
    { label: 'Emotional Therapy Activities', value: '0', positive: true },
    { label: 'Stress Management Activities', value: '0', positive: true },
  ]);
  const [contentStats, setContentStats] = useState([
    { label: 'Total Feeds', value: '0', positive: true },
    { label: 'Total Products', value: '0', positive: true },
  ]);
  const [ticketStats, setTicketStats] = useState([
    { label: 'Total Tickets', value: '0' },
    { label: 'Pending', value: '0' },
    { label: 'Resolved', value: '0' },
    { label: 'Closed', value: '0' },
  ]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const unwrapArray = (res: PromiseSettledResult<any>): any[] => {
          if (res.status === 'rejected') return [];
          const d = res.value.data;
          if (Array.isArray(d?.data)) return d.data;
          if (Array.isArray(d?.results)) return d.results;
          if (Array.isArray(d)) return d;
          return [];
        };

        // Fetch counts from individual endpoints — use allSettled so one failure doesn't zero everything
        const [
          feedsRes,
          notificationsRes,
          expertsRes,
          gutFactsRes,
          nutritionFactsRes,
          sleepFactsRes,
          gutChallengesRes,
          nutritionChallengesRes,
          wellnessStatsRes,
          productsRes,
          ticketsRes,
        ] = await Promise.allSettled([
          axiosPrivate.get('/feeds/'),
          axiosPrivate.get('/notifications/'),
          axiosPrivate.get('/experts/'),
          axiosPrivate.get('/facts/type/gut'),
          axiosPrivate.get('/facts/type/nutrition'),
          axiosPrivate.get('/facts/type/sleep'),
          axiosPrivate.get('/challenges/type/gut'),
          axiosPrivate.get('/challenges/type/nutrition'),
          axiosPrivate.get('/wellness/stats/count-by-type'),
          axiosPrivate.get('/products/'),
          axiosPrivate.get('/support/admin/tickets'),
        ]);

        const users = await fetchAllUsers();

        // Count users (excluding admins and experts)
        const regularUsers = users.filter((user: any) =>
          user.role !== 'admin' && user.role !== 'expert' && user.role !== 'influencer'
        );
        const influencers = users.filter((user: any) => user.role === 'influencer');

        // Count feeds
        const feeds = unwrapArray(feedsRes);

        // Count notifications
        const notifications = unwrapArray(notificationsRes);

        // Count experts from dedicated endpoint
        const experts = unwrapArray(expertsRes);

        // Facts by type
        const gutFacts = unwrapArray(gutFactsRes);
        const nutritionFacts = unwrapArray(nutritionFactsRes);
        const sleepFacts = unwrapArray(sleepFactsRes);

        // Challenges by type
        const gutChallenges = unwrapArray(gutChallengesRes);
        const nutritionChallenges = unwrapArray(nutritionChallengesRes);

        // Wellness stats
        const wellnessStats =
          wellnessStatsRes.status === 'fulfilled'
            ? (wellnessStatsRes.value.data?.data ?? {})
            : {};
        const exerciseCount = wellnessStats.exercise_count || 0;
        const therapyCount = wellnessStats.therapy_count || 0;
        const stressCount = wellnessStats.stress_count || 0;

        // Products
        const products = unwrapArray(productsRes);

        setStats([
          // Stats section
          { 
            label: 'Users', 
            value: regularUsers.length.toString(), 
            positive: true 
          },
          { 
            label: 'Experts', 
            value: experts.length.toString(), 
            positive: true 
          },
          {
            label: 'Influencers',
            value: influencers.length.toString(),
            positive: true
          },
          { 
            label: 'Notifications', 
            value: notifications.length.toString(), 
            positive: true 
          },
          // Guts and wellness
          { label: 'Gut Facts', value: gutFacts.length.toString(), positive: true },
          { label: 'Gut Challenges', value: gutChallenges.length.toString(), positive: true },
          // Nutrition
          { label: 'Nutrition Facts', value: nutritionFacts.length.toString(), positive: true },
          { label: 'Nutrition Challenges', value: nutritionChallenges.length.toString(), positive: true },
          // Sleep and Exercise
          { label: 'Sleep Facts', value: sleepFacts.length.toString(), positive: true },
          { label: 'Exercise Activities', value: exerciseCount.toString(), positive: true },
          { label: 'Emotional Therapy Activities', value: therapyCount.toString(), positive: true },
          { label: 'Stress Management Activities', value: stressCount.toString(), positive: true },
        ]);

        setContentStats([
          { label: 'Total Feeds', value: feeds.length.toString(), positive: true },
          { label: 'Total Products', value: products.length.toString(), positive: true },
        ]);

        // Ticket stats
        const tickets = unwrapArray(ticketsRes);
        setTicketStats([
          { label: 'Total Tickets', value: tickets.length.toString() },
          { label: 'Pending', value: tickets.filter((t: any) => t.status === 'Pending').length.toString() },
          { label: 'Resolved', value: tickets.filter((t: any) => t.status === 'Resolved').length.toString() },
          { label: 'Closed', value: tickets.filter((t: any) => t.status === 'Closed').length.toString() },
        ]);
        
        setLoading(false);
      } catch (error: any) {
        console.error('Dashboard fetch error:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="h-full p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your platform today.</p>
      </div>

      {/* Stats Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800"></h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {stats.slice(0, 4).map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
                  <span className={`ml-2 text-xs font-medium ${stat.positive ? 'text-emerald-700' : 'text-red-600'}`}>
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Support Tickets Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Support Tickets</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {ticketStats.map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Content Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Content</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {contentStats.map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Guts and Wellness Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Guts and Wellness</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {stats.slice(4, 6).map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
                  <span className={`ml-2 text-xs font-medium ${stat.positive ? 'text-emerald-700' : 'text-red-600'}`}>
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Nutrition Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Nutrition</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {stats.slice(6, 8).map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
                  <span className={`ml-2 text-xs font-medium ${stat.positive ? 'text-emerald-700' : 'text-red-600'}`}>
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Sleep and Exercise Section */}
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Sleep and Exercise</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {stats.slice(8, 12).map((stat, index) => (
          <div key={index} className="bg-gradient-to-br from-teal-100 to-emerald-100 p-6 rounded-lg shadow-md border border-teal-300">
            <h3 className="text-teal-800 text-sm font-medium">{stat.label}</h3>
            <div className="flex items-baseline mt-2">
              {loading ? (
                <div className="animate-pulse h-8 w-20 bg-teal-300 rounded"></div>
              ) : (
                <>
                  <p className="text-2xl font-bold text-teal-950">{stat.value}</p>
                  <span className={`ml-2 text-xs font-medium ${stat.positive ? 'text-emerald-700' : 'text-red-600'}`}>
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-lg shadow-md border border-teal-300">
          <div className="px-6 py-4 border-b border-teal-300">
            <h2 className="font-semibold text-teal-950">Recent Activity</h2>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="flex">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-gray-200"></div>
                    </div>
                    <div className="ml-4 w-full">
                      <div className="animate-pulse h-4 w-3/4 bg-gray-200 rounded"></div>
                      <div className="animate-pulse h-3 w-1/4 bg-gray-200 rounded mt-2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.map((activity:any, index) => (
                  <div key={index} className="flex">
                    <div className="flex-shrink-0">
                      <div className={`h-8 w-8 rounded-full ${getActivityIconBg(activity.type)} flex items-center justify-center`}>
                        {getActivityIcon(activity.type)}
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm text-gray-800" dangerouslySetInnerHTML={{ __html: activity.message }}></p>
                      <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(activity.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
            

          </div>
        </div>

        {/* Quick Stats/Tasks */}
        <div className="bg-gradient-to-br from-teal-100 to-emerald-100 rounded-lg shadow-md border border-teal-300">
          <div className="px-6 py-4 border-b border-teal-300">
            <h2 className="font-semibold text-teal-950">Upcoming Tasks</h2>
          </div>
          <div className="p-6">
           
            
            <div className="mt-6">
              <button className="cursor-not-allowed w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                Add New Task
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getActivityIcon = (type:string) => {
  switch (type) {
    case 'user':
      return <User size={16} className="text-blue-600" />;
    case 'content':
      return <FileText size={16} className="text-green-600" />;
    case 'system':
      return <Settings size={16} className="text-purple-600" />;
    case 'users':
      return <Users size={16} className="text-yellow-600" />;
    default:
      return <User size={16} className="text-blue-600" />;
  }
};

const getActivityIconBg = (type: string) => {
  switch (type) {
    case 'user':
      return 'bg-blue-100';
    case 'content':
      return 'bg-green-100';
    case 'system':
      return 'bg-purple-100';
    case 'users':
      return 'bg-yellow-100';
    default:
      return 'bg-blue-100';
  }
};

const formatTimeAgo = (timestamp: string) => {
  const date: any = new Date(timestamp);
  const now: any = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  
  return fmtDate(timestamp);
};

// DD-MM-YYYY HH:MM formatter for display
const fmtDate = (raw: string): string => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default DashboardPage;
